<template>
  <section data-test="filter-address-section">
    <div class="mt-3" v-if="headerTitle">
      <h5 class="text-base font-normal leading-none text-theme-33">
        {{headerTitle}}
      </h5>
    </div>

    <div class="mt-2">
      <label class="from-label text-xxs text-gray-600" for="province-select"
        >จังหวัด</label
      >
      <v-select
        :disabled="disabledProvince"
        data-test="province-select"
        id="province-select"
        :loading="provinceOptionsLoading"
        v-model="provinceComputed"
        :options="provinceOptions"
        label="province_name"
        :clearable="true"
        placeholder="กรุณาเลือกจังหวัด"
        @update:modelValue="handelProvinceSelected"
        :appendToBody="true"
      >
        <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5"/></span>
        </template>
      </v-select>
    </div>

    <div class="mt-2">
      <div class="flex items-center">
        <label
          class="from-label text-xxs text-gray-600 mr-2"
          for="amphur-select"
        >
          เขต/อำเภอ/อปท
        </label>
        <div class="t-tooltip">
          <InfoIcon class="w-4 h-4"/>
          <span class="t-tooltip-text"
            >กรณีที่อยู่ใน อปท ให้เลือก อปท เป็นหลัก
          </span>
        </div>
      </div>
      <v-select
        data-test="amphur-select"
        id="amphur-select"
        :loading="amphurOptionsLoading"
        v-model="amphurComputed"
        :options="amphurOptions"
        label="amphur_name"
        :clearable="true"
        placeholder="กรุณาเลือกเขต/อำเภอ/อปท"
        @update:modelValue="handelAmphurSelected"
        :appendToBody="true"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">
            กรุณาเลือกจังหวัด
          </p>
        </template>
        <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5"/></span>
        </template>
      </v-select>
    </div>

    <div class="mt-2">
      <label class="from-label text-xxs text-gray-600" for="tumbol-select"
        >แขวง/ตำบล</label
      >
      <v-select
        data-test="tumbol-select"
        id="tumbol-select"
        :loading="tumbolOptionsLoading"
        v-model="tumbolComputed"
        :options="tumbolOptions"
        label="tumbol_name"
        :clearable="true"
        placeholder="กรุณาเลือกแขวง/ตำบล"
        @update:modelValue="handelTumbolSelected"
        :appendToBody="true"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">
            กรุณาเลือกเขต/อำเภอ/อปท
          </p>
        </template>
        <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5"/></span>
        </template>
      </v-select>
    </div>

    <div class="mt-2" v-if="!hideVillage">
      <label class="from-label text-xxs text-gray-600" for="village-select"
        >หมู่บ้าน</label
      >
      <v-select
        data-test="village-select"
        id="village-select"
        :loading="villageOptionsLoading"
        v-model="villageComputed"
        :options="villageOptions"
        label="village_name"
        :clearable="true"
        placeholder="กรุณาเลือกหมู่บ้าน"
        :appendToBody="true"
      >
        <template #no-options="">
          <p class="text-sm font-normal text-gray-500">
            กรุณาเลือกแขวง/ตำบล/อปท
          </p>
        </template>
        <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><ChevronDownIcon class="w-5 h-5"/></span>
        </template>
      </v-select>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import axios from "axios";
import Swal from "sweetalert2";
import { useStore } from "vuex";

// declear varible
const props = defineProps({
  province: Object,
  amphur: Object,
  tumbol: Object,
  village: Object,
  parentComponent: {
    type: String,
    require: true,
  },
  headerTitle: {
    type: String,
    default: 'ที่อยู่'
  },
  hideVillage: {
    type: Boolean,
    default: false
  },
  disabledProvince:{
    type: Boolean,
    default: false
  }

});
const emits = defineEmits([
  "update:province",
  "update:amphur",
  "update:tumbol",
  "update:village",
]);

const provinceComputed = computed({
  get() {
    return props.province;
  },
  set(evt) {
    emits("update:province", evt);
  },
});
const amphurComputed = computed({
  get() {
    return props.amphur;
  },
  set(evt) {
    emits("update:amphur", evt);
  },
});
const tumbolComputed = computed({
  get() {
    return props.tumbol;
  },
  set(evt) {
    emits("update:tumbol", evt);
  },
});
const villageComputed = computed({
  get() {
    return props.village;
  },
  set(evt) {
    emits("update:village", evt);
  },
});

const store = useStore();
const provinceOptions = ref([]);
const provinceOptionsLoading = ref(true);
const amphurOptions = ref([]);
const amphurOptionsLoading = ref(false);
const tumbolOptions = ref([]);
const tumbolOptionsLoading = ref(false);
const villageOptions = ref([]);
const villageOptionsLoading = ref(false);
const keyCokeProv = computed(() =>
  parseInt(store.getters["userAuth/getTokenParsed"]["provinceCode"])
);
onMounted(async () => {
  await getProvinceOptions();
  let startProv;
  if (props.parentComponent === "household")
    startProv = await store.getters["householdFilter/province"];
  if (props.parentComponent === "family")
    startProv = await store.getters["familyFilter/province"];
  if (props.parentComponent === "member")
    startProv = await store.getters["memberFilter/province"];
  if (!startProv) {
    if(keyCokeProv.value){
      const temp = await provinceOptions.value.filter(
        (prov) => prov._id.province_ID === keyCokeProv.value
      )[0];
      // if (props.parentComponent === "household") {
      //   await store.dispatch(
      //     "householdFilter/setProvince",
      //     JSON.parse(JSON.stringify(temp))
      //   );
      // } else if (props.parentComponent === "family") {
      //   await store.dispatch(
      //     "familyFilter/setProvince",
      //     JSON.parse(JSON.stringify(temp))
      //   );
      // } else if (props.parentComponent === "member") {
      //   await store.dispatch(
      //     "memberFilter/setProvince",
      //     JSON.parse(JSON.stringify(temp))
      //   );
      // }
      provinceComputed.value = JSON.parse(JSON.stringify(temp));
    }
  }
});

const getProvinceOptions = async () => {
  provinceOptionsLoading.value = true;
  const url = store.getters["userAuth/getUserRoleCode"] !== "tpso" ? `${process.env.VUE_APP_BASE_API}refadms/` : `${process.env.VUE_APP_BASE_API}refadms/?osm=${store.getters["userAuth/getTokenParsed"]["osmCode"]}`;
  const res = await axios.get(url);
  if (res.status === 200) {
    let data = await res.data.sort((a, b) =>
      a.province_name > b.province_name ? 1 : -1
    );
    provinceOptions.value = data;
  } else {
    Swal.fire({
      icon: "warning",
      // title: "",
      text: "ระบบผิดพลาด",
    });
  }
  provinceOptionsLoading.value = false;
};
// getProvinceOptions();

const getAmphurOptions = async () => {
  amphurOptionsLoading.value = true;
  const url = `${process.env.VUE_APP_BASE_API}refadms/?prov=${provinceComputed.value._id.province_ID}`;
  const res = await axios.get(url);
  if (res.status === 200) {
    let data = await res.data.sort((a, b) =>
      a.amphur_name > b.amphur_name ? 1 : -1
    );
    amphurOptions.value = data;
  } else {
    Swal.fire({
      icon: "warning",
      text: "ระบบผิดพลาด",
    });
  }
  amphurOptionsLoading.value = false;
};

const getTumbolOptions = async () => {
  tumbolOptionsLoading.value = true;
  const url = `${process.env.VUE_APP_BASE_API}refadms/?prov=${provinceComputed.value._id.province_ID}&amp=${amphurComputed.value._id.amphur_ID}`;
  const res = await axios.get(url);
  if (res.status === 200) {
    let data = await res.data.sort((a, b) =>
      a.tumbol_name > b.tumbol_name ? 1 : -1
    );
    tumbolOptions.value = data;
  } else {
    Swal.fire({
      icon: "warning",
      text: "ระบบผิดพลาด",
    });
  }
  tumbolOptionsLoading.value = false;
};

const getVillageOptions = async () => {
  villageOptionsLoading.value = true;
  const url = `${process.env.VUE_APP_BASE_API}refadms/?prov=${provinceComputed.value._id.province_ID}&amp=${amphurComputed.value._id.amphur_ID}&tam=${tumbolComputed.value._id.tumbol_ID}`;
  const res = await axios.get(url);
  if (res.status === 200) {
    let data = await res.data.sort((a, b) =>
      a.village_name > b.village_name ? 1 : -1
    );
    data = await data.map((item) => {
      let result = item;
      result.village_name = result.village_name.replace("หมู่ nan ", "");
      return result;
    });
    villageOptions.value = data;
  } else {
    Swal.fire({
      icon: "warning",
      text: "ระบบผิดพลาด",
    });
  }
  villageOptionsLoading.value = false;
};

const handelProvinceSelected = (evt) => {
  amphurComputed.value = null;
  tumbolComputed.value = null;
  villageComputed.value = null;
};

watch(provinceComputed, () => {
  if (provinceComputed.value) {
    getAmphurOptions();
  } else {
    amphurOptions.value = [];
    tumbolOptions.value = [];
    villageOptions.value = [];
  }
});
watch(amphurComputed, () => {
  if (amphurComputed.value) getTumbolOptions();
  else {
    tumbolOptions.value = [];
    villageOptions.value = [];
  }
});
watch(tumbolComputed, () => {
  if (tumbolComputed.value && !props.hideVillage) getVillageOptions();
  else villageOptions.value = [];
});

const handelAmphurSelected = () => {
  tumbolComputed.value = null;
  villageComputed.value = null;
};

const handelTumbolSelected = () => {
  villageComputed.value = null;
};
</script>

<style lang="scss" scoped>
.t-tooltip-text {
  left: -40px;
}
</style>
